import { useState } from 'react';
import { Plus, Play, Pause, Pencil, Clock, ChevronRight, ChevronDown, MoreHorizontal, ArrowLeft, Check } from 'lucide-react';

// ── Pulses ───────────────────────────────────────────────────────────────────
// Work that runs on a schedule. Two surfaces: a list of pulses, and one pulse
// in detail (its brief, its configuration, its run history).
//
// The list leads with the two things you actually check — is it healthy, and
// when does it run next — with a strip of recent outcomes as the quiet proof.
// ─────────────────────────────────────────────────────────────────────────────

type RunStatus = 'completed' | 'failed' | 'running';
interface Run {
  at: string;
  finished: string;
  status: RunStatus;
  duration: string;
  summary: string;
}
interface Pulse {
  id: string;
  name: string;
  kind: string;
  cadence: string;
  next: string;
  nextAbsolute: string;
  destination: string;
  group: string;
  enabled: boolean;
  runCount: number;
  history: RunStatus[];
  prompt: {
    intro: string;
    access: string[];
    steps: string[];
  };
  runs: Run[];
}
const heartbeatRuns = (from: number): Run[] => Array.from({
  length: 12
}, (_, i) => {
  const h = from - Math.floor(i / 2);
  const m = i % 2 === 0 ? '30' : '00';
  return {
    at: `Aug 28, ${String(h).padStart(2, '0')}:${m}`,
    finished: `Aug 28, ${String(h).padStart(2, '0')}:${m}`,
    status: 'completed' as RunStatus,
    duration: `${32 + i * 7 % 30}s`,
    summary: 'No new bookings. 1 call starting inside 4 hours — reminder already sent.'
  };
});
export const PULSES: Pulse[] = [{
  id: 'calendly',
  name: 'Calendly booking notifications',
  kind: 'Heartbeat',
  cadence: 'Every 30 minutes',
  next: 'in 14m',
  nextAbsolute: 'Aug 28, 2026, 12:00',
  destination: '#giga-marketing',
  group: 'Giga Marketing',
  enabled: true,
  runCount: 44,
  history: ['completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: "You are Giga's Calendly booking notifier. Every run you check for newly booked demo calls and calls starting within the next 4 hours, then post updates to the Giga marketing Slack channel. You also maintain a small state file so nothing is announced twice.",
    access: ['Calendly — vault credential `calendly-calendy` via `http_request_with_credential`, API base `https://api.calendly.com`.', "Tony's user URI — `https://api.calendly.com/users/9e10d54a-62c7-4442-9da9`.", 'Slack channel `C0B8PL9GP8B` via `send_slack_message`.', 'State file — track `calendly-booking-notifications`, file `state.json`. If missing, start fresh.'],
    steps: ['Read `state.json` for `last_run_at` and the list of already-reminded event URIs.', 'Fetch active bookings: `GET /scheduled_events?user=<user_uri>&status=active`.', 'Any event created after `last_run_at` is new — fetch its invitees and post “New demo booked”.', 'Any event starting inside 4 hours and not yet reminded — post a reminder and record it.', 'Write `state.json` back with the new timestamp and reminder list.']
  },
  runs: heartbeatRuns(11)
}, {
  id: 'campaign',
  name: 'Company Brain campaign overview',
  kind: 'Daily digest',
  cadence: 'Daily at 09:00 UTC',
  next: 'in 23h',
  nextAbsolute: 'Aug 29, 2026, 09:00',
  destination: '#growth',
  group: 'Giga Marketing',
  enabled: true,
  runCount: 31,
  history: ['completed', 'completed', 'failed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: 'Summarise every live campaign each morning — spend, pipeline created, and anything that moved more than 15% overnight — and post one digest to #growth.',
    access: ['HubSpot — vault credential `hubspot-brightwave`, campaigns and deals endpoints.', 'Google Sheets — spend tracker `Q3 Paid Spend`.', 'Slack channel `#growth` via `send_slack_message`.'],
    steps: ['Pull yesterday’s spend per campaign from the sheet.', 'Pull attributed pipeline per campaign from HubSpot.', 'Flag anything that moved more than 15% against the 7-day average.', 'Post one digest, leading with the flagged campaigns.']
  },
  runs: [{
    at: 'Aug 28, 09:00',
    finished: 'Aug 28, 09:01',
    status: 'completed',
    duration: '1m 12s',
    summary: 'Two campaigns flagged — Search Brand up 22%, Retargeting down 31%.'
  }, {
    at: 'Aug 27, 09:00',
    finished: 'Aug 27, 09:01',
    status: 'completed',
    duration: '58s',
    summary: 'No campaign moved more than 15%. Spend steady at $4.2k/day.'
  }, {
    at: 'Aug 26, 09:00',
    finished: 'Aug 26, 09:00',
    status: 'failed',
    duration: '14s',
    summary: 'HubSpot returned 401 — credential needs reauthorising.'
  }, {
    at: 'Aug 25, 09:00',
    finished: 'Aug 25, 09:01',
    status: 'completed',
    duration: '1m 4s',
    summary: 'Retargeting up 18% after the new creative went live.'
  }]
}, {
  id: 'ambassador',
  name: 'NoContact Ambassador daily recap',
  kind: 'Daily digest',
  cadence: 'Daily at 09:00 UTC',
  next: 'in 23h',
  nextAbsolute: 'Aug 29, 2026, 09:00',
  destination: 'Direct message',
  group: 'NoContact Ops',
  enabled: true,
  runCount: 26,
  history: ['completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'running', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: 'Each morning, recap what the ambassador programme did yesterday — new applications, content published, and referrals converted — and send it as a direct message.',
    access: ['Airtable — `Ambassadors` base, applications and content tables.', 'Stripe — referral conversions via vault credential `stripe-nocontact`.'],
    steps: ['Count new applications and flag any waiting more than 48 hours.', 'List content published, with links.', 'Total referral conversions and revenue.', 'Send one message, applications first.']
  },
  runs: [{
    at: 'Aug 28, 09:00',
    finished: 'Aug 28, 09:00',
    status: 'completed',
    duration: '41s',
    summary: '6 new applications (2 waiting 48h+), 3 posts published, 4 referrals converted.'
  }, {
    at: 'Aug 27, 09:00',
    finished: 'Aug 27, 09:00',
    status: 'completed',
    duration: '38s',
    summary: '3 new applications, 1 post published, 2 referrals converted.'
  }]
}, {
  id: 'renewal',
  name: 'Renewal risk sweep',
  kind: 'Weekly report',
  cadence: 'Fridays at 16:00 UTC',
  next: 'Paused',
  nextAbsolute: '—',
  destination: '#revenue',
  group: 'Giga Master',
  enabled: false,
  runCount: 9,
  history: ['completed', 'completed', 'completed', 'failed', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: 'Every Friday, find accounts renewing inside 60 days with no contact logged in the last 21 days, and post them to #revenue with the owner tagged.',
    access: ['Salesforce — accounts and activity history.', 'Slack channel `#revenue`.'],
    steps: ['Query accounts with a renewal date inside 60 days.', 'Filter to those with no logged activity in 21 days.', 'Sort by contract value, tag the owner, post the list.']
  },
  runs: [{
    at: 'Aug 22, 16:00',
    finished: 'Aug 22, 16:01',
    status: 'completed',
    duration: '1m 33s',
    summary: 'Four accounts flagged, $284k combined. Acme and Northwind both quiet 28 days.'
  }]
}];

// A run outcome as a single tick. Twelve of them read as a health strip
// without needing a chart or a number.
function HistoryStrip({
  history
}: {
  history: RunStatus[];
}) {
  return <span className="flex items-center gap-[3px]" aria-hidden="true" data-id="element-3763" data-mp="22mlotvkak9hu">
      {history.slice(0, 12).reverse().map((s, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${s === 'failed' ? 'bg-[#DC2626]/70' : s === 'running' ? 'bg-[#EA580C]' : 'bg-emerald-500/35'}`} data-id="element-3764" data-mp="26o78q0v8to1j" data-mpi={i} />)}
    </span>;
}
function StatusPill({
  status
}: {
  status: RunStatus;
}) {
  const map = {
    completed: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
    running: 'bg-[#FFF3E9] text-[#B4470F]'
  } as const;
  const label = {
    completed: 'Completed',
    failed: 'Failed',
    running: 'Running'
  } as const;
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${map[status]}`} data-id="element-3765" data-mp="2syfm0rtn3n3u">
      {label[status]}
    </span>;
}

// Turns `code` spans in the brief into real chips, so the prompt reads as a
// document rather than a wall of backticks.
function Prose({
  text
}: {
  text: string;
}) {
  return <span dangerouslySetInnerHTML={{
    __html: text.replace(/`([^`]+)`/g, '<code class="rounded bg-[#F4F0E9] px-1.5 py-0.5 font-mono text-[12.5px] text-[#2B211A]">$1</code>')
  }} data-id="element-3766" data-mp="1w8g5ob78zkap" />;
}
export function PulsesView() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'runs'>('overview');
  const [filter, setFilter] = useState<'enabled' | 'paused' | 'all'>('enabled');
  const [filterOpen, setFilterOpen] = useState(false);
  const pulse = PULSES.find(p => p.id === openId);

  // ── Detail ─────────────────────────────────────────────────────────────────
  if (pulse) {
    return <div className="mx-auto max-w-4xl px-6 pb-16 pt-20 md:pt-16" data-id="element-3767" data-mp="17g4tzskgripl">
        <button type="button" onClick={() => setOpenId(null)} className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-neutral-500 transition-colors hover:text-[#2B211A]" data-id="element-3768" data-mp="tg3ry9sgq58j">
          <ArrowLeft size={13} data-id="element-3769" data-mp="1m3pxvop5xlyk" />
          Pulses
        </button>

        <header className="flex flex-wrap items-start justify-between gap-4 pb-6" data-id="element-3770" data-mp="1m6lvfbx3m8mh">
          <div data-id="element-3771" data-mp="1q51nhu81e76r">
            <h1 className="font-title text-[26px] font-medium tracking-tight text-[#2B211A] md:text-[30px]" data-id="element-3772" data-mp="1q7nv1tuxgmoz">
              {pulse.name}
            </h1>
            <p className="mt-1 text-[14px] text-neutral-500" data-id="element-3773" data-mp="1hchp8uvnq4mg">
              {pulse.kind} · {pulse.cadence}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-3774" data-mp="2510xxbimcjf2">
            <span className="hidden items-center gap-1.5 pr-1 text-[13px] text-neutral-500 sm:flex" data-id="element-3775" data-mp="29uvn2ugztqlj">
              <Clock size={13} className="text-neutral-400" data-id="element-3776" data-mp="19ypnrqjar3st" />
              Next {pulse.next}
            </span>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-xl border border-[#EFEAE1] bg-white px-3 py-2 text-[13.5px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-3777" data-mp="1evfxlw4id35s">
              <Pencil size={13} data-id="element-3778" data-mp="14wrwc90qt2os" />
              Edit
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-3779" data-mp="r65511xbaf2q">
              <Play size={13} data-id="element-3780" data-mp="h9s53mb6b4zj" />
              Run now
            </button>
          </div>
        </header>

        <div className="mb-5 flex items-center gap-5 border-b border-[#EFEAE1]" data-id="element-3781" data-mp="m951axnxpoes">
          {([['overview', 'Overview', null], ['runs', 'Runs', pulse.runCount]] as const).map(([id, label, count]) => <button key={id} type="button" onClick={() => setTab(id)} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[13.5px] font-semibold transition-colors ${tab === id ? 'border-[#2B211A] text-[#2B211A]' : 'border-transparent text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-3782" data-mp="d7oqiot7jka1">
                {label}
                {count !== null && <span className="text-[12.5px] font-medium text-neutral-400" data-id="element-3783" data-mp="2nmph6q4tq9t">
                    {count}
                  </span>}
              </button>)}
        </div>

        {tab === 'overview' ? <div className="grid gap-4 lg:grid-cols-[1fr_260px]" data-id="element-3784" data-mp="ciwrhugj5xnl">
            {/* The brief, as a readable document. */}
            <div className="rounded-2xl border border-[#EFEAE1] bg-white p-6" data-id="element-3785" data-mp="7jaenm4m6jfb">
              <div className="flex items-center justify-between gap-3" data-id="element-3786" data-mp="m9rfb39fd5b5">
                <h2 className="text-[15px] font-semibold text-[#2B211A]" data-id="element-3787" data-mp="hay096ehp68v">
                  Brief
                </h2>
                <span className="rounded-full bg-[#F4F0E9] px-2.5 py-0.5 text-[11.5px] font-semibold text-neutral-600" data-id="element-3788" data-mp="vbqxhyw6effo">
                  {pulse.kind}
                </span>
              </div>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[#2B211A]" data-id="element-3789" data-mp="3k55jq8vjpw4l">
                {pulse.prompt.intro}
              </p>

              <h3 className="mt-6 border-t border-[#F4F0E9] pt-4 text-[11.5px] font-bold text-[#A2917F]" data-id="element-3790" data-mp="3p0uoqrqc458a">
                What it can reach
              </h3>
              <ul className="mt-2.5 space-y-2" data-id="element-3791" data-mp="1qwkaeccchp4">
                {pulse.prompt.access.map(a => <li key={a} className="flex gap-2.5 text-[13.5px] leading-relaxed text-neutral-600" data-id="element-3792" data-mp="6phd3sp9jz3j">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-neutral-300" data-id="element-3793" data-mp="bn9fxhrg55mi" />
                    <span data-id="element-3794" data-mp="gmm3k4q56gq5">
                      <Prose text={a} data-id="element-3795" data-mp="gogmjdhuryrl" />
                    </span>
                  </li>)}
              </ul>

              <h3 className="mt-6 border-t border-[#F4F0E9] pt-4 text-[11.5px] font-bold text-[#A2917F]" data-id="element-3796" data-mp="ko7al9fzw0sf">
                What it does each run
              </h3>
              <ol className="mt-2.5 space-y-2.5" data-id="element-3797" data-mp="2mfkfhyhyq8oh">
                {pulse.prompt.steps.map((s, i) => <li key={s} className="flex gap-3 text-[13.5px] leading-relaxed text-neutral-600" data-id="element-3798" data-mp="2ewb45zpwbzws" data-mpi={i}>
                    <span className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#F4F0E9] text-[11px] font-bold text-neutral-500" data-id="element-3799" data-mp="2a64fy0zaqhf2">
                      {i + 1}
                    </span>
                    <span data-id="element-3800" data-mp="2167lfiq86oc3">
                      <Prose text={s} data-id="element-3801" data-mp="2170votoe7vyz" />
                    </span>
                  </li>)}
              </ol>
            </div>

            <aside className="rounded-2xl border border-[#EFEAE1] bg-white p-5" data-id="element-3802" data-mp="2xt2rqlch116n">
              <h2 className="text-[11.5px] font-bold text-[#A2917F]" data-id="element-3803" data-mp="2su9emu13jc70">Configuration</h2>
              <dl className="mt-3 space-y-3" data-id="element-3804" data-mp="2osxmagqu8cv4">
                {[['Status', pulse.enabled ? 'Enabled' : 'Paused'], ['Schedule', pulse.cadence], ['Next run', pulse.enabled ? `${pulse.next} · ${pulse.nextAbsolute}` : '—'], ['Posts to', pulse.destination], ['Group', pulse.group], ['Runs so far', String(pulse.runCount)]].map(([k, v]) => <div key={k} data-id="element-3805" data-mp="18ivbq955yb07">
                    <dt className="text-[12px] text-neutral-400" data-id="element-3806" data-mp="2wcoq1t5qezj4">{k}</dt>
                    <dd className="mt-0.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-3807" data-mp="2wbdrc7r58hyv">
                      {v}
                    </dd>
                  </div>)}
              </dl>
              <button type="button" className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#EFEAE1] py-2 text-[13px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-3808" data-mp="2ned3q3bwenwh">
                {pulse.enabled ? <>
                    <Pause size={13} data-id="element-3809" data-mp="3k315dp3b3yjq" />
                    Pause pulse
                  </> : <>
                    <Play size={13} data-id="element-3810" data-mp="363t50sveqkdy" />
                    Resume pulse
                  </>}
              </button>
            </aside>
          </div> : <ul className="overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white" data-id="element-3811" data-mp="3b09vvutzrcuq">
            {pulse.runs.map(r => <li key={r.at} className="flex items-start gap-4 border-b border-[#F4F0E9] px-5 py-3.5 last:border-b-0" data-id="element-3812" data-mp="1upxslrvw0pqf">
                <span className="min-w-0 flex-1" data-id="element-3813" data-mp="1r5xdr5q8wsxz">
                  <span className="flex flex-wrap items-center gap-2" data-id="element-3814" data-mp="1r4cfp64q2rvd">
                    <span className="text-[14px] font-semibold text-[#2B211A]" data-id="element-3815" data-mp="1n53n44lf501e">
                      {r.at}
                    </span>
                    <StatusPill status={r.status} data-id="element-3816" data-mp="213br2wgvh1m2" />
                  </span>
                  <span className="mt-1 block text-[13px] leading-relaxed text-neutral-500" data-id="element-3817" data-mp="1w61u8vr5lno7">
                    {r.summary}
                  </span>
                </span>
                <span className="shrink-0 text-right" data-id="element-3818" data-mp="13guvpwrp8y2y">
                  <span className="block text-[12px] text-neutral-400" data-id="element-3819" data-mp="13i5xxs5hxxry">
                    {r.duration}
                  </span>
                </span>
              </li>)}
          </ul>}
      </div>;
  }

  // ── List ───────────────────────────────────────────────────────────────────
  const shown = PULSES.filter(p => filter === 'all' || (filter === 'enabled' ? p.enabled : !p.enabled));
  return <div className="mx-auto max-w-4xl px-6 pb-16 pt-20 md:pt-16" data-id="element-3820" data-mp="kpbrxnpupfzv">
      <header className="flex flex-wrap items-end justify-between gap-4 pb-6" data-id="element-3821" data-mp="2ksw63ca58sag">
        <div data-id="element-3822" data-mp="2pqxr42r4r1ok">
          <h1 className="font-title text-[26px] font-medium tracking-tight text-[#2B211A] md:text-[30px]" data-id="element-3823" data-mp="2pqe80ipqu78y">
            Pulses
          </h1>
          <p className="mt-1.5 text-[14.5px] text-neutral-500" data-id="element-3824" data-mp="2uiphodaad7ep">
            Work that runs on a schedule without being asked.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-3825" data-mp="21ts42nh0rysa">
          <div className="relative" data-id="element-3826" data-mp="21vbi07f7dxqw">
            <button type="button" onClick={() => setFilterOpen(o => !o)} aria-expanded={filterOpen} className="inline-flex items-center gap-1.5 rounded-xl border border-[#EFEAE1] bg-white px-3 py-2 text-[13.5px] font-medium text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-3827" data-mp="26s20f1ehd5fh">
              {filter === 'enabled' ? 'Enabled' : filter === 'paused' ? 'Paused' : 'All'}
              <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${filterOpen ? 'rotate-180' : ''}`} data-id="element-3828" data-mp="3mbg5bzdwlnsc" />
            </button>
            {filterOpen && <>
                <div className="fixed inset-0 z-30" onClick={() => setFilterOpen(false)} data-id="element-3829" data-mp="58trnuo2omir" />
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[150px] overflow-hidden rounded-xl border border-[#EFEAE1] bg-white py-1 shadow-[0_20px_50px_-20px_rgba(43,33,26,0.35)]" data-id="element-3830" data-mp="8efh6fj1l2d">
                  {(['enabled', 'paused', 'all'] as const).map(f => <button key={f} type="button" onClick={() => {
                setFilter(f);
                setFilterOpen(false);
              }} className="flex w-full items-center justify-between px-3 py-2 text-left text-[13.5px] font-medium text-[#2B211A] transition-colors hover:bg-[#FFF7F0]" data-id="element-3831" data-mp="3im5ctnpe1r20">
                      {f === 'enabled' ? 'Enabled' : f === 'paused' ? 'Paused' : 'All'}
                      {filter === f && <Check size={13} strokeWidth={3} className="text-[#EA580C]" data-id="element-3832" data-mp="16v1njpskqkjl" />}
                    </button>)}
                </div>
              </>}
          </div>
          <button type="button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-3833" data-mp="1m37swngranyx">
            <Plus size={14} strokeWidth={2.5} data-id="element-3834" data-mp="1q6xj1hd2xdm2" />
            New pulse
          </button>
        </div>
      </header>

      {shown.length === 0 ? <div className="rounded-2xl border border-[#EFEAE1] bg-white px-6 py-12 text-center" data-id="element-3835" data-mp="1hbzne2ijrmr1">
          <p className="text-[14px] font-semibold text-[#2B211A]" data-id="element-3836" data-mp="12eqnulj3bco8">
            Nothing {filter === 'paused' ? 'paused' : 'enabled'}
          </p>
          <p className="mx-auto mt-1.5 max-w-[42ch] text-[13px] leading-relaxed text-neutral-500" data-id="element-3837" data-mp="17fmsj1b9n2up">
            A pulse is a job Giga runs on a schedule — a morning digest, a
            weekly sweep, a check every half hour.
          </p>
        </div> : <ul className="overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white" data-id="element-3838" data-mp="2nsuvoy4s2oq3">
          {shown.map(p => <li key={p.id} data-id="element-3839" data-mp="2ss7ri8dmn64t">
              <button type="button" onClick={() => {
          setOpenId(p.id);
          setTab('overview');
        }} className="flex w-full items-center gap-4 border-b border-[#F4F0E9] px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-[#FFFCF9]" data-id="element-3840" data-mp="2sqd50pmpj5ym">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${p.enabled ? 'bg-emerald-500' : 'bg-neutral-300'}`} data-id="element-3841" data-mp="3bogd4z56r86v" />
                <span className="min-w-0 flex-1" data-id="element-3842" data-mp="36w58xk3ex08i">
                  <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3843" data-mp="3lm473muywbnz">
                    {p.name}
                  </span>
                  <span className="mt-0.5 block truncate text-[12.5px] text-neutral-500" data-id="element-3844" data-mp="3gm82bleg4g7d">
                    {p.cadence} · {p.destination}
                  </span>
                </span>
                <span className="hidden shrink-0 sm:block" data-id="element-3845" data-mp="3qkxj1aulvum1">
                  <HistoryStrip history={p.history} data-id="element-3846" data-mp="208ro828dfz3f" />
                </span>
                <span className="hidden w-[76px] shrink-0 text-right text-[12.5px] text-neutral-500 sm:block" data-id="element-3847" data-mp="2091fdt55kcap">
                  {p.enabled ? p.next : 'Paused'}
                </span>
                <ChevronRight size={15} className="shrink-0 text-neutral-300" data-id="element-3848" data-mp="13jmvn6e5gad7" />
              </button>
            </li>)}
        </ul>}

      <p className="mt-3 px-1 text-[12px] text-neutral-400" data-id="element-3849" data-mp="yka9knv3r7bo">
        Each strip shows the last twelve runs, oldest on the left.
      </p>
    </div>;
}